import { Box, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { Fragment, useState } from "react";
import { getTranslation } from "../../utils/translate";
import useSWR from "swr";
import { fetcher } from "../../common/utils/fetcher";
import KeyValueCard from "../KeyValueCard";
import { chooseInputComponent } from "../../common/utils/components";
import DataVisualiser from "../DataVisualiser";
import Loader from "../Loader";
import DownloadPointsButton from "./DownloadPointsButton";

const ContestInsights = () => {
    const retailer = useSelector((state) => state.retailer);
    const [contestId, setContestId] = useState('cd992816-30d5-40c9-8092-0755c8eff0cd');

    const { data: contestData } = useSWR({
        url: 'contests',
        fields: 'id,achievements.*',
        filter: {
            _and: [
                {
                    id: {
                        _eq: contestId,
                    }
                },
            ],
        },
    }, fetcher);

    let contest = null;

    if (contestData) {
        contest = contestData[0];
    }

    const { data: achievementsData } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: 'achievement',
    }, fetcher);

    let achievements = null;

    if (achievementsData) {
        achievements = achievementsData.map(achievement => (
            {
                ...achievement,
            }
        ));
    }

    const { data: provincesData, isLoading: provincesDataLoading } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: 'province',
    }, fetcher);

    let provinces = null;
    if (provincesData) {
        provinces = provincesData;
    }

    const { data: registrationsData } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
                {

                }
            ],
        },
        aggregate: {
            count: '*',
        },
    }, fetcher);

    const { data: newsletterSubscriptionsData } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
            ],
        },
        deep: {
            _filter: {
                short_code: {
                    receive_updates: {
                        _eq: true,
                    }
                }
            }
        },
        aggregate: {
            count: '*',
        },
    }, fetcher);

    let registrationsCount = 0;
    let newsletterSubscriptionsCount = 0;

    if (registrationsData) {
        registrationsCount = registrationsData[0].count;
    }

    if (newsletterSubscriptionsData) {
        newsletterSubscriptionsCount = newsletterSubscriptionsData[0].count;
    }

    const formSections = [  
        {
            title: '',
            fields: [
                {
                    label: '',
                    name: 'contest_id',
                    type: 'm2o',
                    value: contestId,
                    handleInputChange: (value) => {
                        setContestId(value);
                    },
                    config: {
                        size: '12',
                        translations: [],
                        title: 'Contests',
                        buttonText: 'Select Contest',
                        url: '/contests',
                        searchOnly: 'CONTESTS',
                        detailsPayload: {
                            fields: '*,translations.*',
                        },
                        detailsUrl: `/contests`,
                        detailsResponseHandler: (data) => {
                            const translatedItem = getTranslation(data, { key: 'languages_code', code: 'en-US' })

                            return {
                                id: data?.id,
                                title: translatedItem?.tagline || "",
                                description: translatedItem?.heading || "",
                                imageUrl: `${process.env.REACT_APP_API_URL}/assets/${translatedItem?.featured_image}?key=brand-portal-image-card-square`,
                                url: `/contests/${data.id}`,
                                data,
                            };
                        },
                        payload: {
                            fields: '*, translations.*',
                            limit: 25,
                            meta: '*',
                            filter: {
                                _or: [
                                    {
                                        retailers: {
                                            retailer_id: {
                                                _eq: retailer.id,
                                            }
                                        }
                                    },
                                ],
                            },
                        },
                        rows: [],
                        responseHandler: (data) => {
                            return data?.data?.map((item) => {
                                const translatedItem = getTranslation(item, { key: 'languages_code', code: 'en-US' })
                                return {
                                    id: item?.id,
                                    title: translatedItem?.tagline || "",
                                    description: translatedItem?.heading || "",
                                    imageUrl: `${process.env.REACT_APP_API_URL}/assets/${translatedItem?.featured_image}`,
                                    url: `/contests/${item.id}`,
                                    data: item,
                                };
                            }) || [];
                        },
                        headCells: [],
                        items: [],
                    },
                }, 
            ],
        },
    ];

    return (
        <Box>
            <Box>
                {formSections.map((section) => (
                    <Box key={section.title}>
                        {section.title && (
                            <Typography
                                className="form-section-title"
                                variant="h4"
                            >
                                {section.title}
                            </Typography>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                                <Box sx={{ flexGrow: 1 }}>
                                    {section.fields.map((field) => (
                                        <Fragment key={field.name}>
                                            <Grid
                                                key={field.name}
                                                item
                                                {...(["colour"].includes(field?.type)
                                                    ? {
                                                        xs: 12,
                                                        md: 4,
                                                    }
                                                    : {
                                                        xs: 12,
                                                    })}
                                            >
                                                {chooseInputComponent(field, 'en-US')}
                                            </Grid>
                                        </Fragment>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Box>

            <Grid container
                spacing={2}
            >
                {
                    achievements?.map((achievement) => (
                        <Grid
                            key={achievement.achievement}
                            item
                            xs={12}
                            md={6}
                            lg={4}
                        >
                            <KeyValueCard
                                label={achievement.achievement}
                                variant="statistic"
                                value={achievement.count.toLocaleString()}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                dynamicLabel={{
                                    detailsPayload: {
                                        fields: '*,translations.*',
                                    },
                                    detailsUrl: `achievements/${achievement.achievement}`,
                                    detailsResponseHandler: (data) => {
                                        const translatedItem = getTranslation(data, { key: 'languages_code', code: 'en-US' })

                                        return {
                                            id: data?.id,
                                            title: translatedItem?.title || "",
                                        };
                                    },
                                }}
                            >
                                <DownloadPointsButton achievementId={achievement.achievement} />
                            </KeyValueCard>
                        </Grid>
                    ))
                }
            </Grid>

            <Box>
                {
                    provincesDataLoading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <Loader />
                        </Box>
                    ) : (
                        <>
                            <DataVisualiser
                                data={provinces?.filter(province => !!province?.province)}
                                xKey="province"
                                yKey="count"
                                xLabel="Province"
                                yLabel="Count"
                                title="Province Registrations"
                                description="Contest registrations by province"
                            />
                        </>
                    )
                }
            </Box>
        </Box >
    )
}

export default ContestInsights